/**
 * Privacy Policy modal component.
 */

import { useTranslation } from 'react-i18next';
import { Modal, Button } from 'react-bootstrap';

interface PrivacyPolicyModalProps {
  show: boolean;
  onHide: () => void;
}

// Privacy Policy content embedded directly
const PRIVACY_POLICY_CONTENT = `# Privacy Policy

**Last Updated: November 2024**

## Introduction

Myant Inc. ("Myant", "we", "us", or "our") operates the Data Shop platform (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.

By using the Data Shop platform, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our Service.

## Information We Collect

### Personal Information

We collect personal information that you provide to us, including:

- **Account Information**: Your name, email address, and Google account information when you authenticate through Google OAuth
- **Profile Information**: Phone number and other profile details you provide
- **Usage Data**: Information about how you use the Service, including datasets you browse and orders you place

### Automatically Collected Information

We may automatically collect certain information about your device when you access the Service, including:

- Device information and identifiers
- Log data and usage statistics
- IP address and location data

## How We Use Your Information

We use the information we collect to:

- **Provide and Maintain the Service**: Process your orders, manage your account, and deliver requested data
- **Authenticate Users**: Verify your identity and manage access to the Service
- **Communication**: Send you updates, notifications, and support communications related to your use of the Service
- **Improve the Service**: Analyze usage patterns to enhance functionality and user experience
- **Compliance**: Comply with legal obligations and enforce our terms of service

## Data Sharing and Disclosure

We do not sell your personal information. We may share your information in the following circumstances:

- **With Your Consent**: When you have given explicit consent to share information
- **Service Providers**: With trusted third-party service providers who assist in operating our Service (e.g., hosting, analytics)
- **Legal Requirements**: When required by law, court order, or governmental authority
- **Business Transfers**: In connection with a merger, acquisition, or sale of assets, where user information may be transferred
- **Protection of Rights**: To protect the rights, property, or safety of Myant, our users, or others

## Data Security

We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.

## Data Retention

We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When information is no longer needed, we will securely delete or anonymize it.

## Your Rights and Choices

Depending on your location, you may have certain rights regarding your personal information:

- **Access**: Request access to the personal information we hold about you
- **Correction**: Request correction of inaccurate or incomplete information
- **Deletion**: Request deletion of your personal information
- **Portability**: Request transfer of your data to another service
- **Objection**: Object to certain processing activities
- **Withdrawal of Consent**: Withdraw consent where processing is based on consent

To exercise these rights, please contact us using the information provided in the "Contact Us" section below.

## Third-Party Services

The Service may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to read the privacy policies of any third-party services you access.

## Children's Privacy

Our Service is not intended for users under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child under 18, we will take steps to delete such information promptly.

## International Data Transfers

Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. By using the Service, you consent to the transfer of your information to these countries.

## Changes to This Privacy Policy

We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.

## Cookies and Tracking Technologies

We use cookies and similar tracking technologies to track activity on our Service and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.

## Contact Us

If you have any questions about this Privacy Policy or our privacy practices, please contact us:

**Myant Inc.**
Email: privacy@myant.ca
Website: https://myant.ca

## Consent

By using our Service, you consent to our Privacy Policy and agree to its terms.`;

export default function PrivacyPolicyModal({ show, onHide }: PrivacyPolicyModalProps) {
  const { t } = useTranslation();

  // Parse markdown to HTML (simple conversion)
  const parseMarkdown = (markdown: string): string => {
    // Process line by line to handle different elements correctly
    const lines = markdown.split('\n');
    const processedLines: string[] = [];
    let inList = false;

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];
      const trimmedLine = line.trim();

      // Skip empty lines (they'll create paragraph breaks)
      if (!trimmedLine) {
        if (inList) {
          processedLines.push('</ul>');
          inList = false;
        }
        processedLines.push('');
        continue;
      }

      // Headers (must be processed first)
      if (trimmedLine.startsWith('#### ')) {
        if (inList) {
          processedLines.push('</ul>');
          inList = false;
        }
        processedLines.push(`<h4>${trimmedLine.substring(5).trim()}</h4>`);
        continue;
      }
      if (trimmedLine.startsWith('### ')) {
        if (inList) {
          processedLines.push('</ul>');
          inList = false;
        }
        processedLines.push(`<h3>${trimmedLine.substring(4).trim()}</h3>`);
        continue;
      }
      if (trimmedLine.startsWith('## ')) {
        if (inList) {
          processedLines.push('</ul>');
          inList = false;
        }
        processedLines.push(`<h2>${trimmedLine.substring(3).trim()}</h2>`);
        continue;
      }
      if (trimmedLine.startsWith('# ')) {
        if (inList) {
          processedLines.push('</ul>');
          inList = false;
        }
        processedLines.push(`<h1>${trimmedLine.substring(2).trim()}</h1>`);
        continue;
      }

      // Lists
      if (trimmedLine.startsWith('- ')) {
        if (!inList) {
          processedLines.push('<ul>');
          inList = true;
        }
        let listItem = trimmedLine.substring(2).trim();
        // Process inline formatting in list items
        listItem = listItem.replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>');
        listItem = listItem.replace(/\*(.*?)\*/gim, '<em>$1</em>');
        listItem = listItem.replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
        processedLines.push(`<li>${listItem}</li>`);
        continue;
      }

      // Regular paragraph text
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }

      // Process inline formatting
      line = trimmedLine;
      line = line.replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>');
      line = line.replace(/\*(.*?)\*/gim, '<em>$1</em>');
      line = line.replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

      // Only wrap in paragraph if not already HTML
      if (!line.trim().startsWith('<')) {
        processedLines.push(`<p>${line}</p>`);
      } else {
        processedLines.push(line);
      }
    }

    if (inList) {
      processedLines.push('</ul>');
    }

    // Join lines and clean up
    let html = processedLines.join('\n');
    // Clean up multiple empty lines
    html = html.replace(/\n{3,}/gim, '\n\n');

    return html;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{t('modals.privacyPolicy.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div
          style={{
            maxHeight: '60vh',
            overflowY: 'auto',
            padding: '1rem',
            lineHeight: '1.6',
            color: 'var(--myant-text-primary)',
          }}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: parseMarkdown(PRIVACY_POLICY_CONTENT),
            }}
            style={{
              fontFamily: 'var(--myant-font-primary)',
            }}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onHide}>
          {t('common.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
